import { FC, useMemo, useCallback } from 'react';
import { css } from '@emotion/react';
import { UserDetails } from '@weave/schema-gen-ts/dist/schemas/auth-api/v3/auth.pb';
import { Creator } from '@weave/schema-gen-ts/dist/schemas/task-center/shared/v1/task.pb';
import { Icon, IconName } from '@frontend/icons';
import { getFullName } from '@frontend/name';
import { usePopoverDialog, PopoverDialog, Text, Button, Dot } from '@frontend/design-system';
import { AIR_RECEPTIONIST_MOCK_ID } from '../../../constants';
import { useGetCreatedByList } from '../../../hooks/use-get-created-by-list';
import { generateKey, itemValue, generateTrackingId } from '../../../utils/task-person-filter-utils';
import { PersonSearchableListbox } from '../../person-searchable-listbox';
import { filtersButtonStyle } from '../common-styles';
import { PersonRenderItem } from './person-render-item';
import { iconLeftSpacing, popoverStyle } from './task-person-filter.styles';

interface TaskPersonFilterProps {
  setValue: (creator: Creator) => void;
  selectedValue: Creator | null;
  label: string;
  iconName?: IconName;
  trackingId: string;
}

export const TaskPersonFilter: FC<TaskPersonFilterProps> = ({
  setValue,
  selectedValue,
  label,
  iconName,
  trackingId,
}) => {
  const createdByList = useGetCreatedByList();
  const { getDialogProps, getTriggerProps, close } = usePopoverDialog();

  const listboxValue = useMemo(() => {
    if (!selectedValue) return '';
    if ('userId' in selectedValue) return selectedValue.userId;
    if ('air' in selectedValue) return AIR_RECEPTIONIST_MOCK_ID;
    return '';
  }, [selectedValue]);

  const memoizedPersonRenderItem = useCallback((user: UserDetails) => <PersonRenderItem user={user} />, []);

  const onSelect = useCallback(
    (value: string | string[]) => {
      const selectedId = Array.isArray(value) ? value[0] ?? '' : value;
      const val: Creator = selectedId === AIR_RECEPTIONIST_MOCK_ID ? { air: {} } : { userId: selectedId };
      setValue(val);
      close();
    },
    [close, setValue]
  );

  return (
    <span>
      <Button variant='secondary' css={filtersButtonStyle} {...getTriggerProps()} trackingId={trackingId}>
        <Icon name={iconName ?? 'user'} size={16} />
        <Text weight='regular'>{label}</Text>
        {selectedValue && <Dot color='primary' css={css({ position: 'relative' })} />}
        <Icon name='alt-caret-right-tiny' color='subdued' css={iconLeftSpacing} />
      </Button>
      <PopoverDialog {...getDialogProps()} css={popoverStyle}>
        <PersonSearchableListbox<UserDetails>
          data={createdByList}
          trackingIdGen={generateTrackingId}
          renderItem={memoizedPersonRenderItem}
          keyGen={generateKey}
          getItemValue={itemValue}
          value={listboxValue}
          onSelect={onSelect}
          getSearchValue={(item) => getFullName(item)}
        />
      </PopoverDialog>
    </span>
  );
};
